import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { post, saveSession, C, THUMB_BASE, LOGO_BASE } from "./utils";
import ProductProfileScreen from "./Product_profile";
import AppShell, { ThemeCtx, NAV_ITEMS, hasAccess, SORT_OPTIONS, Icon } from "./AppShell";
import ClinicalTimeline from "./ClinicalTimeline";
import EventsTimeline from "./EventsTimeline";
import TimelinesScreen from "./Timelines";
import EchoScreen from "./Echo";
import AtAGlance from "./AtAGlance";

// ─── Doc type constants ───────────────────────────────────────────────────────
const openDocTab = (doc, subId, stateid=2) => {
  const base = window.location.origin + window.location.pathname;
  localStorage.setItem(`doc_stateid_${doc.DOCUMENTID}`, String(stateid));
  const url=`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`;
  if(window.innerWidth<768) window.location.href=url;
  else window.open(url,"_blank","noopener,noreferrer");
};

const GALLERY_TYPES       = "89,88,87,86,85,84,83,82,91";
export const NEWS_TYPES    = "95";
export const REPORTS_TYPES = "92";
export const KITS_TYPES    = "90";
const CLINICAL_TRIALS_TYPES = "96";
const EVENTS_TYPES        = "94";
const PAGE_SIZE           = 20;

const isImage   = (p = "") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName  = (p = "") => p.split("/").pop();
const stripHtml = (html = "") => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"\"').replace(/\s+/g, " ").trim();
};

const getPrimaryProduct    = (products = []) => products.find(p => (p.PRIMARY === "1" || p.PRIMARY === 1) && String(p.BRANDID) !== "0")?.BRANDNAME || "";
const getPrimaryIndication = (inds = [])     => inds.find(i => (i.PRIMARY === "1" || i.PRIMARY === 1) && String(i.INDICATIONID) !== "0")?.INDICATIONNAME || "";
const getCompanyName       = (c)             => (c && String(c.SPONSORID) !== "0") ? c.SPONSORNAME || "" : "";
const getCompanyLogo       = (c)             => (c && String(c.SPONSORID) !== "0" && c.COMPANY_LOGO) ? LOGO_BASE + c.COMPANY_LOGO : null;

// ─── Shared UI components ─────────────────────
function ScreenHeader({ title, subtitle, searchInput, onSearch, sort, onSort, placeholder }) {
  const isMobile = useContext(ThemeCtx)?.isMobile || false;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
      <div>
        <div style={{ fontSize: "19px", fontWeight: "500", color: C.text }}>{title}</div>
        <div style={{ fontSize: "13px", color: C.muted, marginTop: "1px" }}>{subtitle}</div>
      </div>
      {!isMobile&&<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", background: "#fff", border: `1px solid ${C.border}`, borderRadius: "8px", padding: "0 10px", gap: "6px", height: "34px" }}>
          <Icon name="search" size={13} color={C.hint} />
          <input value={searchInput} onChange={e => onSearch(e.target.value)} placeholder={placeholder || "Search..."}
            style={{ border: "none", outline: "none", fontSize: "14px", color: C.text, background: "none", width: "160px" }} />
        </div>
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <select value={sort} onChange={e => onSort(e.target.value)}
            style={{ padding: "6px 28px 6px 10px", borderRadius: "8px", border: `1px solid ${C.border}`, background: "#fff", color: C.muted, fontSize: "13px", outline: "none", cursor: "pointer", appearance: "none" }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ position: "absolute", right: "8px", pointerEvents: "none" }}><Icon name="chevron" size={12} color={C.hint} /></div>
        </div>
      </div>}
    </div>
  );
}

const Spinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "2rem", color: C.muted, fontSize: "14px" }}>
    <div style={{ width: "16px", height: "16px", border: `2px solid ${C.border}`, borderTopColor: C.primary, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    Loading...
    <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
  </div>
);

const LoadMore = ({ onClick }) => (
  <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
    <button onClick={onClick} style={{ padding: "9px 28px", borderRadius: "8px", border: `1px solid ${C.border}`, background: "#fff", color: C.primary, fontSize: "14px", fontWeight: "500", cursor: "pointer" }}>
      Load more
    </button>
  </div>
);

const StatRow = ({ fav, attach, likes, imgCount, comments }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    {fav && <Icon name="star" size={11} color="#EF9F27" />}
    {[{ n: "attach", v: attach }, { n: "like", v: likes }, { n: "image", v: imgCount }, { n: "chat", v: comments }].map(s => (
      <div key={s.n} style={{ display: "flex", alignItems: "center", gap: "2px", fontSize: "12px", color: C.hint }}>
        <Icon name={s.n} size={11} color={C.hint} /><span>{s.v}</span>
      </div>
    ))}
  </div>
);

// ─── Shared data hook ─────────────────────────
// ─── Drag-scroll hook ─────────────────────────
function useDragScroll() {
  const ref  = useRef(null);
  const drag = useRef({ on:false, x:0, sx:0 });
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const down = e => { drag.current={on:true,x:e.clientX,sx:el.scrollLeft}; el.style.cursor="grabbing"; el.style.userSelect="none"; };
    const move = e => { if (!drag.current.on) return; el.scrollLeft=drag.current.sx-(e.clientX-drag.current.x); };
    const up   = () => { drag.current.on=false; el.style.cursor="grab"; el.style.userSelect=""; };
    el.addEventListener("mousedown",down);
    el.addEventListener("mousemove",move);
    el.addEventListener("mouseup",up);
    el.addEventListener("mouseleave",up);
    return () => {
      el.removeEventListener("mousedown",down);
      el.removeEventListener("mousemove",move);
      el.removeEventListener("mouseup",up);
      el.removeEventListener("mouseleave",up);
    };
  });
  return ref;
}

// ─── Hashtag filter bar ────────────────────────
function HashtagBar({ hashtags, active, onChange }) {
  const ref = useDragScroll();
  if (!hashtags.length) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"12px" }}>
      <span style={{ fontSize:"10px", fontWeight:"700", color:"#9CA3AF",
        textTransform:"uppercase", letterSpacing:"0.5px", flexShrink:0 }}>Tags</span>
      <div ref={ref} style={{ flex:1, minWidth:0, overflowX:"auto", overflowY:"hidden",
        scrollbarWidth:"none", msOverflowStyle:"none", cursor:"grab",
        WebkitOverflowScrolling:"touch", userSelect:"none" }}>
        <div style={{ display:"flex", gap:"6px", width:"max-content", padding:"2px 0" }}>
          <button onMouseDown={e=>e.stopPropagation()} onClick={()=>onChange(null)}
            style={{ padding:"3px 12px", borderRadius:"20px", flexShrink:0,
              border:`1.5px solid ${!active?"#6366F1":"#E5E7EB"}`,
              background:!active?"#EEF2FF":"#fff", color:!active?"#6366F1":"#6B7280",
              fontSize:"11px", fontWeight:!active?"700":"500", cursor:"pointer" }}>
            All
          </button>
          {hashtags.map(tag => {
            const a = active === tag;
            return (
              <button key={tag} onMouseDown={e=>e.stopPropagation()} onClick={()=>onChange(a?null:tag)}
                style={{ padding:"3px 12px", borderRadius:"20px", flexShrink:0, whiteSpace:"nowrap",
                  border:`1.5px solid ${a?"#6366F1":"#E5E7EB"}`,
                  background:a?"#EEF2FF":"#fff", color:a?"#6366F1":"#6B7280",
                  fontSize:"11px", fontWeight:a?"700":"500", cursor:"pointer", transition:"all 0.15s" }}>
                {tag.replace(/^#/,"")}
              </button>
            );
          })}
        </div>
      </div>
      {active && (
        <button onClick={()=>onChange(null)}
          style={{ padding:"3px 10px", borderRadius:"20px", border:"1px solid #FECACA",
            background:"#FEF2F2", color:"#EF4444", fontSize:"11px",
            fontWeight:"600", cursor:"pointer", flexShrink:0 }}>✕</button>
      )}
    </div>
  );
}

function useDocLoader({ user, subproject, docTypes, stateid = 2, extraParams = {}, defaultSort }) {
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError]     = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch]   = useState("");
  const [sort, setSort]       = useState(defaultSort||"latest");
  const [hashtags, setHashtags] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const pageRef        = useRef(0);
  const subRef         = useRef(null);
  const searchTimer    = useRef(null);
  const activeTagRef   = useRef(null);
  // Keep extraParams in a ref — always current, never triggers re-renders
  const extraParamsRef    = useRef(extraParams);
  extraParamsRef.current  = extraParams;
  // Stable string used only to detect past↔upcoming switches
  const extraParamsKey    = JSON.stringify(extraParams);
  const prevExtraKey      = useRef(extraParamsKey);

  const loadDocs = useCallback(async (reset = false, overrides = {}) => {
    if (!subproject) return;
    setLoading(true); setError(null);
    const first  = reset ? 0 : pageRef.current * PAGE_SIZE;
    const params = { subprojectid: subproject.SUBPROJECTID, userid: user.USERID, doctypeid: docTypes, stateid, first, last: PAGE_SIZE, sort, ...extraParamsRef.current, ...overrides };
    if (search && !overrides.search) params.search = search;
    if (activeTagRef.current && !overrides.search) params.search = activeTagRef.current;
    try {
      const r = await post("advance_filter", params, user.AUTH_CODE);
      if (r.STATUS === "1" && Array.isArray(r.DATA) && r.DATA.length > 0) {
        setDocs(prev => reset ? r.DATA : [...prev, ...r.DATA]);
        setHasMore(r.DATA.length === PAGE_SIZE);
        pageRef.current = reset ? 1 : pageRef.current + 1;
        // Collect hashtags from root + per-doc
        if (reset) {
          const all = new Set();
          if (Array.isArray(r.HASHTAGS)) r.HASHTAGS.forEach(t => all.add(t));
          r.DATA.forEach(doc => (Array.isArray(doc.HASHTAGS) ? doc.HASHTAGS : []).forEach(t => all.add(t)));
          if (all.size > 0) setHashtags([...all].sort());
        }
      } else {
        if (reset) setDocs([]);
        setHasMore(false);
      }
    } catch { setError("Failed to load data."); }
    setLoading(false);
  }, [subproject, user, search, sort, docTypes, stateid]);

  // Reload when subproject or docType changes
  useEffect(() => {
    subRef.current = subproject?.SUBPROJECTID;
    pageRef.current = 0; setDocs([]); setHasMore(true);
    loadDocs(true);
  }, [subproject?.SUBPROJECTID, docTypes]);

  // Reload only when extraParams actually changes (e.g. past ↔ upcoming)
  useEffect(() => {
    if (extraParamsKey === prevExtraKey.current) return;
    prevExtraKey.current = extraParamsKey;
    pageRef.current = 0; setDocs([]); setHasMore(true);
    loadDocs(true);
  }, [extraParamsKey]);

  const handleSearch = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val); pageRef.current = 0; setDocs([]); setHasMore(true);
      loadDocs(true, { search: val });
    }, 500);
  };

  const handleSort = (val) => {
    setSort(val); pageRef.current = 0; setDocs([]); setHasMore(true);
    loadDocs(true, { sort: val });
  };

  const handleTag = (tag) => {
    activeTagRef.current = tag;
    setActiveTag(tag);
    pageRef.current = 0; setDocs([]); setHasMore(true);
    loadDocs(true);
  };

  return { docs, loading, hasMore, error, searchInput, sort, hashtags, activeTag, handleSearch, handleSort, handleTag, loadMore: () => loadDocs(false) };
}

// ─── Gallery tile (original compact card layout) ─────────────────────────────
export function GalleryTile({ doc, showDocType = true, onOpen }) {
  const [hovered, setHovered] = useState(false);
  const chatbot    = Array.isArray(doc.CHATBOT)     ? doc.CHATBOT     : [];
  const company    = Array.isArray(doc.COMPANY)     ? doc.COMPANY[0]  : null;
  const products   = Array.isArray(doc.PRODUCTS)    ? doc.PRODUCTS    : [];
  const indications= Array.isArray(doc.INDICATIONS) ? doc.INDICATIONS : [];
  const imgFiles   = chatbot.filter(a => isImage(a.FILEPATH));
  const thumbUrl   = imgFiles.length > 0 ? THUMB_BASE + fileName(imgFiles[0].FILEPATH) : null;
  const logoUrl    = getCompanyLogo(company);
  const sponsor    = getCompanyName(company);
  const product    = getPrimaryProduct(products);
  const indication = getPrimaryIndication(indications);
  const notes      = stripHtml(doc.NOTES || "");
  const docType    = doc.DOCTYPENAME || "";
  const date       = doc.DISPLAY_TIME || "";
  const fav        = doc.MYFAVOURITES === "YES";

  const ctx      = useContext(ThemeCtx) || {};
  const theme    = ctx.theme || ctx || {};
  const grad     = theme.tileGrad || `linear-gradient(180deg, ${C.primary} 0%, #1a6fba 100%)`;
  const tPrimary = theme.primary  || C.primary;

  return (
    <div onClick={onOpen?()=>onOpen(doc):undefined} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: "#fff", borderRadius: "10px", border: `1px solid ${hovered ? tPrimary : C.border}`, overflow: "hidden", display: "flex", flexDirection: "row", cursor: "pointer", transform: hovered ? "translateY(-2px)" : "none", transition: "all 0.15s", boxShadow: hovered ? `0 4px 20px ${tPrimary}22` : "none" }}>

      {/* Left gradient accent bar */}
      <div style={{ width: "4px", background: grad, flexShrink: 0 }} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "flex-start", padding: "10px", gap: "8px" }}>
          {/* Company logo + sponsor name */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flexShrink: 0, width: "40px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.light, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", width: "36px" }}>
              {logoUrl ? <img src={logoUrl} alt={sponsor} style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
                : sponsor ? <span style={{ fontSize: "12px", fontWeight: "600", color: tPrimary }}>{sponsor[0]}</span>
                : <Icon name="image" size={14} color={C.border} />}
            </div>
            {sponsor && <div style={{ fontSize: "10px", color: C.muted, textAlign: "center", lineHeight: "1.2", wordBreak: "break-word", maxWidth: "40px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{sponsor}</div>}
          </div>
          {/* Product: Title / Title */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {showDocType && <div style={{ fontSize: "11px", color: C.mid, fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.4px" }}>{docType}</div>}
            <div style={{ fontSize: "13px", fontWeight: "500", color: C.text, lineHeight: "1.35", marginTop: "2px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {product ? <><span style={{ color: tPrimary }}>{product}:</span>{" "}{doc.SUBJECT || ""}</> : (doc.SUBJECT || "Untitled")}
            </div>
            {indication && <div style={{ fontSize: "12px", color: C.muted, marginTop: "1px" }}>{indication}</div>}
          </div>
          {/* Thumbnail — top right */}
          <div style={{ width: "60px", height: "60px", borderRadius: "6px", border: `1px solid ${C.border}`, background: C.light, flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {thumbUrl ? <img src={thumbUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: "2px" }} onError={e => e.target.style.display = "none"} />
              : <Icon name="image" size={16} color={C.border} />}
          </div>
        </div>
        {/* Notes */}
        {notes && <div style={{ padding: "0 10px 6px", fontSize: "12px", color: C.muted, lineHeight: "1.45", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{notes}</div>}
        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", padding: "6px 10px 8px", borderTop: `1px solid #EEF4FB`, marginTop: "auto" }}>
          <span style={{ fontSize: "11px", color: C.hint, flex: 1 }}>{date}</span>
          <StatRow fav={fav} attach={doc.COUNT || 0} likes={doc.LIKES || 0} imgCount={doc.IMAGECOUNT || 0} comments={doc.COMMENTCOUNT || 0} />
        </div>
      </div>
    </div>
  );
}

// ─── Gallery screen ───────────────────────────
export function GalleryScreen({ user, subproject, defaultSort }) {
  return <ListScreen user={user} subproject={subproject} title="Gallery" docTypes={GALLERY_TYPES} TileComponent={GalleryTile} showDocType={true} defaultSort={defaultSort} />;
}


// ─── Clinical Trials tile ─────────────────────────────────────────────────────
function ClinicalTile({ doc, onOpen }) {
  const [hovered, setHovered] = useState(false);
  const chatbot    = Array.isArray(doc.CHATBOT)     ? doc.CHATBOT     : [];
  const company    = Array.isArray(doc.COMPANY)     ? doc.COMPANY[0]  : null;
  const products   = Array.isArray(doc.PRODUCTS)    ? doc.PRODUCTS    : [];
  const indications= Array.isArray(doc.INDICATIONS) ? doc.INDICATIONS : [];
  const imgFiles   = chatbot.filter(a => isImage(a.FILEPATH));
  const thumbUrl   = imgFiles.length > 0 ? THUMB_BASE + fileName(imgFiles[0].FILEPATH) : null;
  const logoUrl    = getCompanyLogo(company);
  const sponsor    = getCompanyName(company);
  const product    = getPrimaryProduct(products);
  const indication = getPrimaryIndication(indications);
  const notes      = stripHtml(doc.NOTES || "");
  const docType    = doc.DOCTYPENAME || "";
  const date       = doc.DISPLAY_TIME || "";
  const fav        = doc.MYFAVOURITES === "YES";
  const acronym = (typeof doc.ACRONYMS === "string" && doc.ACRONYMS) ? doc.ACRONYMS : "";

  const ctx      = useContext(ThemeCtx) || {};
  const theme    = ctx.theme || ctx || {};
  const grad     = theme.tileGrad || `linear-gradient(180deg, ${C.primary} 0%, #1a6fba 100%)`;
  const tPrimary = theme.primary  || C.primary;

  return (
    <div onClick={onOpen?()=>onOpen(doc):undefined} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: "#fff", borderRadius: "10px", border: `1px solid ${hovered ? tPrimary : C.border}`, overflow: "hidden", display: "flex", flexDirection: "row", cursor: "pointer", transform: hovered ? "translateY(-2px)" : "none", transition: "all 0.15s", boxShadow: hovered ? `0 4px 20px ${tPrimary}22` : "none" }}>

      {/* Left gradient accent bar */}
      <div style={{ width: "4px", background: grad, flexShrink: 0 }} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "flex-start", padding: "10px", gap: "8px" }}>
          {/* Company logo + sponsor name */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flexShrink: 0, width: "40px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.light, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              {logoUrl ? <img src={logoUrl} alt={sponsor} style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
                : sponsor ? <span style={{ fontSize: "12px", fontWeight: "600", color: tPrimary }}>{sponsor[0]}</span>
                : <Icon name="image" size={14} color={C.border} />}
            </div>
            {sponsor && <div style={{ fontSize: "10px", color: C.muted, textAlign: "center", lineHeight: "1.2", wordBreak: "break-word", maxWidth: "40px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{sponsor}</div>}
          </div>
          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {acronym && <span style={{ display: "inline-block", fontSize: "11px", fontWeight: "700", color: "#B45309", background: "#FEF3C7", borderRadius: "4px", padding: "1px 8px", letterSpacing: "0.4px", marginBottom: "2px" }}>{acronym}</span>}
            <div style={{ fontSize: "13px", fontWeight: "500", color: C.text, lineHeight: "1.35", marginTop: "2px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {product ? <><span style={{ color: tPrimary }}>{product}:</span>{" "}{doc.SUBJECT || ""}</> : (doc.SUBJECT || "Untitled")}
            </div>
            {indication && <div style={{ fontSize: "12px", color: C.muted, marginTop: "1px" }}>{indication}</div>}
          </div>
          {/* Thumbnail */}
          <div style={{ width: "60px", height: "60px", borderRadius: "6px", border: `1px solid ${C.border}`, background: C.light, flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {thumbUrl ? <img src={thumbUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: "2px" }} onError={e => e.target.style.display = "none"} />
              : <Icon name="image" size={16} color={C.border} />}
          </div>
        </div>
        {/* Notes */}
        {notes && <div style={{ padding: "0 10px 6px", fontSize: "12px", color: C.muted, lineHeight: "1.45", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{notes}</div>}
        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", padding: "6px 10px 8px", borderTop: `1px solid #EEF4FB`, marginTop: "auto" }}>
          <span style={{ fontSize: "11px", color: C.hint, flex: 1 }}>{date}</span>
          <StatRow fav={fav} attach={doc.COUNT || 0} likes={doc.LIKES || 0} imgCount={doc.IMAGECOUNT || 0} comments={doc.COMMENTCOUNT || 0} />
        </div>
      </div>
    </div>
  );
}

// ─── Generic list screen ─────────────────────
export function ListScreen({ user, subproject, title, docTypes, TileComponent = GalleryTile, gridCols = "repeat(auto-fill, minmax(220px, 1fr))", showDocType = false, extraParams = {}, defaultSort, stateid = 2 }) {
  const { docs, loading, hasMore, error, searchInput, sort, hashtags, activeTag, handleSearch, handleSort, handleTag, loadMore } = useDocLoader({ user, subproject, docTypes, extraParams, defaultSort, stateid });
  return (
    <div style={{ padding: "20px" }}>
      <ScreenHeader title={title} subtitle={`${subproject?.SUMMARY} · ${docs.length} items`} searchInput={searchInput} onSearch={handleSearch} sort={sort} onSort={handleSort} placeholder={`Search ${title.toLowerCase()}...`} />
      <HashtagBar hashtags={hashtags} active={activeTag} onChange={handleTag} />
      {error && <div style={{ padding: "10px 14px", borderRadius: "8px", fontSize: "14px", marginBottom: "1rem", background: C.errorBg, color: C.error }}>{error}</div>}
      {docs.length === 0 && !loading && (
        <div style={{ textAlign: "center", padding: "4rem 0" }}>
          <Icon name="attach" size={40} color={C.border} />
          <div style={{ fontSize: "15px", color: C.muted, marginTop: "1rem" }}>No {title.toLowerCase()} found</div>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: "12px" }}>
        {docs.map(doc => <TileComponent key={doc.DOCUMENTID} doc={doc} showDocType={showDocType} onOpen={d=>openDocTab(d,subproject?.SUBPROJECTID,stateid)}/>)}
      </div>
      {loading && <Spinner />}
      {hasMore && !loading && docs.length > 0 && <LoadMore onClick={loadMore} />}
    </div>
  );
}

// ─── Sidebar nav item ─────────────────────────
function NavItem({ item, active, onClick, theme = {} }) {
  const [hovered, setHovered] = useState(false);
  const bar      = theme.primary || "#185FA5";
  const activeColor = bar;
  const activeBg    = `${bar}14`;   /* 8% opacity tint */
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: "10px",
        width: "100%", padding: "8px 14px", border: "none",
        borderLeft: `3px solid ${active ? activeColor : "transparent"}`,
        background: active ? activeBg : hovered ? "rgba(0,0,0,0.04)" : "transparent",
        color: active ? activeColor : "#4A5568",
        fontSize: "14px", fontWeight: active ? "600" : "400",
        cursor: "pointer", textAlign: "left", transition: "all 0.1s",
      }}>
      <Icon name={item.icon} size={15} color="currentColor" />
      {item.label}
    </button>
  );
}


// ─── Dashboard ────────────────────────────────
export default function Dashboard({ session, onLogout }) {
  if (!session?.user) return null;
  return <AppShell session={session} onLogout={onLogout} />;
}

// ─── Clinical Trials screen (list + timeline toggle) ─────────────────────────
export function ClinicalTrialsScreen({ user, subproject }) {
  const [view, setView] = useState("list");
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* View toggle */}
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 20px 0", gap: "6px" }}>
        {[{ id: "list", label: "☰ List" }, { id: "timeline", label: "▦ Timeline" }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            style={{ padding: "5px 14px", borderRadius: "8px", border: "1px solid #E5E7EB",
              background: view === v.id ? "#6366F1" : "#fff",
              color:      view === v.id ? "#fff" : "#6B7280",
              fontSize: "12px", fontWeight: "600", cursor: "pointer", transition: "all 0.15s" }}>
            {v.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: view === "list" ? "auto" : "hidden" }}>
        {view === "list"
          ? <ListScreen user={user} subproject={subproject} title="Clinical Trials"
              docTypes={CLINICAL_TRIALS_TYPES} TileComponent={ClinicalTile} />
          : <ClinicalTimeline user={user} subproject={subproject} />
        }
      </div>
    </div>
  );
}

// ─── Events screen — list by default + timeline toggle ───────────────────────
export function EventsScreen({ user, subproject, eventtype }) {
  const [view, setView] = useState("list");
  const title = eventtype === "past" ? "Past Events" : "Upcoming Events";
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* View toggle */}
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 20px 0", gap: "6px" }}>
        {[{ id: "list", label: "☰ List" }, { id: "timeline", label: "▦ Timeline" }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            style={{ padding: "5px 14px", borderRadius: "8px", border: "1px solid #E5E7EB",
              background: view === v.id ? "#6366F1" : "#fff",
              color:      view === v.id ? "#fff" : "#6B7280",
              fontSize: "12px", fontWeight: "600", cursor: "pointer", transition: "all 0.15s" }}>
            {v.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: view === "list" ? "auto" : "hidden" }}>
        {view === "list"
          ? <ListScreen user={user} subproject={subproject} title={title}
              docTypes={EVENTS_TYPES} extraParams={{ eventtype }} />
          : <EventsTimeline user={user} subproject={subproject} initialType={eventtype} />
        }
      </div>
    </div>
  );
}

// ─── Content router ───────────────────────────